import React from 'react';

import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@apollo/client';
import {
  Flatfile,
  FlatfileRecord,
  PartialRejection,
  RecordError,
} from '@flatfile/sdk';

import { getClient } from 'lane-shared/apollo';
import { isRequired } from 'lane-shared/domains/visitorManagement/features/VisitorManagement';
import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { FlatfileToken } from 'lane-shared/graphql/flatfile';
import { VisitorManagementFeatureProperties } from 'lane-shared/types/features/VisitorManagementFeatureProperties';
import { VisitorManagementFields } from 'lane-shared/types/upload/VisitorManagementFields';
import {
  NO_VISITOR_PROVIDERS,
  VisitorField,
  VisitorProvider,
  validateVisitorInput,
} from 'domains-visitor-validation';

import styles from '../../content-blocks/AddVisitorsBlock.scss';

const isEmpty = (val: any) => val === null || val === undefined || val === '';

const textValue = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

/** Legacy importer column for each visitor field that integrations validate. */
const LEGACY_FIELD_KEYS: Partial<Record<VisitorField, string>> = {
  firstName: 'visitorFirstName',
  lastName: 'visitorLastName',
};

/**
 * Splits an uploaded chunk into rows to import and rows to send back to the
 * Flatfile importer with field errors, so hosts can fix values the building's
 * visitor integrations won't accept without re-uploading the whole file.
 */
export function partitionLegacyBulkRecords(
  records: FlatfileRecord[],
  visitorProviders: ReadonlyArray<VisitorProvider>,
  translator: (key: string, params?: any) => string
): { accepted: FlatfileRecord[]; rejected: RecordError[] } {
  if (visitorProviders.length === 0) {
    return { accepted: records, rejected: [] };
  }

  const accepted: FlatfileRecord[] = [];
  const rejected: RecordError[] = [];

  for (const record of records) {
    const errors = validateVisitorInput(
      {
        firstName: textValue(record.data.visitorFirstName),
        lastName: textValue(record.data.visitorLastName),
      },
      visitorProviders,
      translator
    ).map(({ field, message }) => ({
      field: LEGACY_FIELD_KEYS[field] ?? field,
      message,
    }));

    if (errors.length) {
      rejected.push(new RecordError(record.recordId, errors));
    } else {
      accepted.push(record);
    }
  }

  return { accepted, rejected };
}

export function VisitorInviteBulkUploadLegacy({
  properties,
  handleBulkAdd,
  isPreview = false,
  visitorProviders = NO_VISITOR_PROVIDERS,
  visitorProvidersLoading = false,
}: {
  properties: VisitorManagementFeatureProperties;
  handleBulkAdd: (visitors: VisitorType[]) => void;
  isPreview?: boolean;
  /** The building's visitor integrations, whose rules uploaded rows must satisfy. */
  visitorProviders?: ReadonlyArray<VisitorProvider>;
  /** While the providers are loading, disable import so onData uses their rules. */
  visitorProvidersLoading?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const getFlatfilePortal = (): string => {
    if (
      isRequired(properties.emailSetting) &&
      isRequired(properties.phoneSetting) &&
      isRequired(properties.companySetting)
    ) {
      return VisitorManagementFields.ALL_FIELDS_REQUIRED;
    }

    if (
      isRequired(properties.emailSetting) &&
      isRequired(properties.phoneSetting)
    ) {
      return VisitorManagementFields.NAME_EMAIL_CELL_REQUIRED;
    }

    if (
      isRequired(properties.emailSetting) &&
      isRequired(properties.companySetting)
    ) {
      return VisitorManagementFields.NAME_EMAIL_COMPANY_REQUIRED;
    }

    if (
      isRequired(properties.phoneSetting) &&
      isRequired(properties.companySetting)
    ) {
      return VisitorManagementFields.NAME_CELL_COMPANY_REQUIRED;
    }

    if (isRequired(properties.emailSetting)) {
      return VisitorManagementFields.NAME_EMAIL_REQUIRED;
    }

    if (isRequired(properties.phoneSetting)) {
      return VisitorManagementFields.NAME_CELL_REQUIRED;
    }

    if (isRequired(properties.companySetting)) {
      return VisitorManagementFields.NAME_COMPANY_REQUIRED;
    }

    return VisitorManagementFields.NAME_REQUIRED;
  };

  const flatfilePortal = getFlatfilePortal();

  const { data } = useQuery(FlatfileToken, {
    // @ts-ignore (known TS issue re apollo client)
    client: getClient(),
    fetchPolicy: 'no-cache',
    variables: {
      portal: flatfilePortal,
    },
  });
  const jwtToken: string | null | undefined = data?.flatfileToken?.token;

  const mapBulkRecords = (record: FlatfileRecord): VisitorType => {
    return {
      email: record.data.visitorEmail as string | undefined,
      firstName: record.data.visitorFirstName as string | undefined,
      lastName: record.data.visitorLastName as string | undefined,
      company: record.data.visitorCompany as string | undefined,
      phone:
        !isEmpty(record.data.visitorCountryCode) ||
        !isEmpty(record.data.visitorCellNumber)
          ? `+${record.data.visitorCountryCode}${record.data.visitorCellNumber}`
          : '',
    };
  };

  const importData = () => {
    Flatfile.requestDataFromUser({
      embedId: data?.flatfileToken?.embedId,
      token: jwtToken || undefined,
      onData(chunk, next) {
        const records: FlatfileRecord[] = chunk.records;
        const { accepted, rejected } = partitionLegacyBulkRecords(
          records,
          visitorProviders,
          t
        );

        if (accepted.length) {
          handleBulkAdd(accepted.map(mapBulkRecords));
        }

        next(rejected.length ? new PartialRejection(rejected) : undefined);
      },
      onComplete() {},
      onError({ error }) {
        window.Toast.show(error.userMessage, 5000);
      },
    }).catch(error => {
      window.Toast.show(error.message, 5000);
    });
  };

  const handleOnClick = () => {
    importData();
  };

  return (
    <Button
      onClick={handleOnClick}
      className={styles.bulkUploadButton}
      disabled={isPreview || visitorProvidersLoading}
      testId="inviteBulkUpload"
    >
      {t('web.content.feature.visitorManagement.visitorInvite.bulkUpload')}
    </Button>
  );
}